import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, FormField, required } from '@angular/forms/signals';
import {
  CreatePackageRequestV1,
  OptionResponseV1,
  PackageDisplay,
  PackageResponseV1,
  PricingFrequency,
  ProductLine
} from '@membership/membership';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';

import { DialogMode } from '@core/models/models';
import { MembershipService } from '@core/services/membership';

interface PackageFormModel {
  name: string;
  description: string;
  categoryCode: string;
  shorthandCode: string;
  baselinePrice: number;
  productLine: ProductLine;
  oneTimeFee: number | null;
  pricingFrequency: PricingFrequency;
  optionIds: string[];
  isActive: boolean;
}

@Component({
  selector: 'app-packages-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    FormField,
    ButtonModule,
    TagModule,
    TooltipModule,
    DialogModule,
    SelectModule,
    MultiSelectModule,
    IconField,
    InputIcon,
    InputTextModule,
    TextareaModule,
    InputNumberModule,
    ProgressSpinnerModule
  ],
  templateUrl: './packages-table.html',
  styleUrl: './packages-table.scss'
})
export class PackagesTableComponent {
  private readonly membershipService = inject(MembershipService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  // Resources - each component instance gets its own
  readonly packagesResource = this.membershipService.createPackagesResource();
  readonly optionsResource = this.membershipService.createOptionsResource();

  // Local signals
  readonly searchTerm = signal<string>('');
  readonly showDialog = signal<boolean>(false);
  readonly dialogMode = signal<DialogMode>(DialogMode.Create);
  readonly selectedPackageId = signal<string | null>(null);
  readonly deletingId = signal<string | null>(null);
  readonly saving = signal<boolean>(false);

  readonly packageFormModel = signal<PackageFormModel>(this.emptyPackageForm());

  readonly packageForm = form(this.packageFormModel, (schemaPath) => {
    required(schemaPath.name, { message: 'Package name is required' });
    required(schemaPath.categoryCode, { message: 'Category code is required' });
    required(schemaPath.shorthandCode, { message: 'Shorthand code is required' });
  });

  readonly productLineOptions = signal([
    { label: 'Marketplace', value: ProductLine.Marketplace },
    { label: 'Health', value: ProductLine.Health }
  ]);

  readonly pricingFrequencyOptions = signal([
    { label: 'Once', value: PricingFrequency.Once },
    { label: 'Startup', value: PricingFrequency.Startup },
    { label: 'Monthly', value: PricingFrequency.Monthly },
    { label: 'Quarterly', value: PricingFrequency.Quarterly },
    { label: 'Semi-Annual', value: PricingFrequency.SemiAnnual },
    { label: 'Annual', value: PricingFrequency.Annual }
  ]);

  readonly columns = signal<{ field: string; header: string; sortable: boolean }[]>([
    { field: 'name', header: 'Package Name', sortable: true },
    { field: 'packageCode', header: 'Code', sortable: true },
    { field: 'description', header: 'Description', sortable: false },
    { field: 'baselinePrice', header: 'Price', sortable: true },
    { field: 'options', header: 'Options', sortable: false },
    { field: 'isActive', header: 'Status', sortable: true },
    { field: 'createdOn', header: 'Created', sortable: true },
    { field: 'actions', header: 'Actions', sortable: false }
  ]);

  readonly filteredPackages = computed<PackageDisplay[]>(() => {
    const searchTerm = this.searchTerm().toLowerCase();
    const packages: PackageDisplay[] = this.packagesResource.value() ?? [];
    if (!searchTerm) return packages;

    return packages.filter(
      (pkg: PackageDisplay) =>
        pkg.name.toLowerCase().includes(searchTerm) || pkg.description?.toLowerCase().includes(searchTerm)
    );
  });

  readonly dialogTitle = computed(() => (this.dialogMode() === DialogMode.Create ? 'Create Package' : 'Edit Package'));

  readonly dialogButtonLabel = computed(() => (this.dialogMode() === DialogMode.Create ? 'Create' : 'Update'));

  readonly isLoading = computed(() => this.packagesResource.isLoading() || this.optionsResource.isLoading());

  readonly options = computed<OptionResponseV1[]>(() => this.optionsResource.value() ?? []);

  readonly selectedPackageCode = computed<string | null>(() => {
    const id = this.selectedPackageId();
    if (!id) return null;
    const packages = this.packagesResource.value() ?? [];
    return packages.find((p) => p.packageId === id)?.packageCode ?? null;
  });

  readonly isEditMode = computed(() => this.dialogMode() === DialogMode.Edit);

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
  }

  onCreatePackage(): void {
    this.dialogMode.set(DialogMode.Create);
    this.selectedPackageId.set(null);
    this.packageFormModel.set(this.emptyPackageForm());
    this.showDialog.set(true);
  }

  async onEditPackage(pkg: PackageDisplay): Promise<void> {
    this.dialogMode.set(DialogMode.Edit);
    this.selectedPackageId.set(pkg.packageId);

    const baseModel: PackageFormModel = {
      name: pkg.name,
      description: pkg.description || '',
      categoryCode: pkg.categoryCode,
      shorthandCode: pkg.shorthandCode,
      baselinePrice: pkg.baselinePrice,
      productLine: pkg.productLine,
      oneTimeFee: pkg.oneTimeFee ?? null,
      pricingFrequency: pkg.pricingFrequency,
      optionIds: [],
      isActive: pkg.isActive
    };

    try {
      const packageOptions = await firstValueFrom(this.membershipService.getPackageOptions(pkg.packageId));
      this.packageFormModel.set({
        ...baseModel,
        optionIds: packageOptions.map((opt) => opt.optionId)
      });
    } catch (error) {
      console.error('Failed to load package options:', error);
      this.packageFormModel.set(baseModel);
    }
    this.showDialog.set(true);
  }

  async onSavePackage(): Promise<void> {
    if (!this.isFormValid()) {
      this.showError('Please fix validation errors');
      return;
    }

    this.saving.set(true);
    const formData = this.packageFormModel();
    const payload: CreatePackageRequestV1 = {
      name: formData.name,
      description: formData.description || undefined,
      categoryCode: formData.categoryCode,
      shorthandCode: formData.shorthandCode,
      baselinePrice: formData.baselinePrice,
      productLine: formData.productLine,
      oneTimeFee: formData.oneTimeFee ?? undefined,
      pricingFrequency: formData.pricingFrequency,
      isActive: formData.isActive,
      optionIds: formData.optionIds
    };

    const operation =
      this.dialogMode() === DialogMode.Create
        ? this.membershipService.createPackage(payload)
        : this.membershipService.updatePackage(this.selectedPackageId()!, payload as Partial<PackageResponseV1>);

    try {
      await firstValueFrom(operation);
      this.showSuccess(`Package ${this.dialogMode() === DialogMode.Create ? 'created' : 'updated'} successfully`);
      this.showDialog.set(false);
      this.packagesResource.reload();
    } catch (error) {
      this.showError('Failed to save package');
      console.error('Save package error:', error);
    } finally {
      this.saving.set(false);
    }
  }

  onDeletePackage(pkg: PackageDisplay): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete package "${pkg.name}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deletePackage(pkg)
    });
  }

  private async deletePackage(pkg: PackageDisplay): Promise<void> {
    this.deletingId.set(pkg.packageId);

    try {
      await firstValueFrom(this.membershipService.deletePackage(pkg.packageId));
      this.showSuccess(`Package ${pkg.name} deleted successfully`);
      this.packagesResource.reload();
    } catch (error) {
      this.showError('Failed to delete package');
      console.error('Delete package error:', error);
    } finally {
      this.deletingId.set(null);
    }
  }

  getStatusSeverity(isActive: boolean): 'success' | 'danger' {
    return isActive ? 'success' : 'danger';
  }

  getStatusLabel(isActive: boolean): string {
    return isActive ? 'Active' : 'Inactive';
  }

  isFormValid(): boolean {
    const model = this.packageFormModel();
    return !!(
      model.name.trim() &&
      model.categoryCode.trim() &&
      model.shorthandCode.trim() &&
      model.baselinePrice >= 0 &&
      this.packageForm.name().valid() &&
      this.packageForm.categoryCode().valid() &&
      this.packageForm.shorthandCode().valid()
    );
  }

  onOptionsChange(optionIds: string[]) {
    this.packageFormModel.set({
      ...this.packageFormModel(),
      optionIds
    });
  }

  onProductLineChange(productLine: ProductLine) {
    this.packageFormModel.set({
      ...this.packageFormModel(),
      productLine
    });
  }

  onPricingFrequencyChange(pricingFrequency: PricingFrequency) {
    this.packageFormModel.set({
      ...this.packageFormModel(),
      pricingFrequency
    });
  }

  formatCurrency(amount: number | null | undefined): string {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  }

  private emptyPackageForm(): PackageFormModel {
    return {
      name: '',
      description: '',
      categoryCode: '',
      shorthandCode: '',
      baselinePrice: 0,
      productLine: ProductLine.Marketplace,
      oneTimeFee: null,
      pricingFrequency: PricingFrequency.Once,
      optionIds: [],
      isActive: true
    };
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
  }

  private showSuccess(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message
    });
  }
}
